// Pure, DOM-free helpers for the platform-look R2 player. Extracted from the
// component so the resume-key / resume-time math and time formatting are unit
// testable without mounting a <video> (jsdom mounting is not set up in this
// repo — see the __tests__ note).
//
// Style note: NO optional chaining (`?.`) / nullish coalescing (`??`) — the
// Node 12 build tooling chokes on them, so the whole r2/ tree avoids them and
// mirrors video-providers/r2/ instead (bitten in prod, 2026-07-30).

// Resume position is persisted under the SAME key the Plyr r2 provider uses
// (`_dmvideo_<id>`), so a lesson watched through either player resumes from the
// other. Keyed by the LMS material id (`videoId` prop), never the R2 lessonId.
export const RESUME_KEY_PREFIX = "_dmvideo_";

export function resumeKey(videoId) {
    return `${RESUME_KEY_PREFIX}${videoId}`;
}

// Parse a persisted resume position. localStorage stores it as a string; return
// a finite, non-negative number of seconds, or null when there is nothing
// usable to resume from (missing / empty / NaN / <= 0).
export function parseResumeTime(raw) {
    if (raw === null || raw === undefined || raw === "") return null;
    const value = Number(raw);
    if (!isFinite(value) || value <= 0) return null;
    return value;
}

// "MM:SS" — mirrors the Vimeo donor's formatter, with an added guard so a
// not-yet-known native duration (NaN before loadedmetadata) renders "00:00"
// instead of "NaN:NaN".
export function formatTime(seconds) {
    const total = isFinite(seconds) && seconds > 0 ? seconds : 0;
    const minutes = Math.floor(total / 60);
    const secs = Math.floor(total % 60);
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

// ---- Subtitles (side-loaded VTT from the play payload) ----------------------
//
// The BFF passes the video-service payload through unchanged; its textTracks
// items are `{ lang, label, src }` (see profile-new videoPlayRoute.test.js +
// createHtml5Adapter). Ukrainian off label matches profile-new's R2 subtitle
// path («Вимк»).
export const SUBTITLE_OFF_LABEL = "Вимк";

// Map the payload textTracks into the menu list the donor markup expects
// (`{ language, label }` — the Vimeo skin reads `.language` / `.label`). Real
// tracks keep their `src` for the <track> elements; an "off" sentinel
// (language:"") is appended so the user can turn captions off. Items missing a
// src or lang are dropped. Returns [] when there is nothing to show, so the
// (v-if="subtitleTracks.length") button stays hidden.
export function mapSubtitleTracks(payloadTracks) {
    const list = Array.isArray(payloadTracks) ? payloadTracks : [];
    const real = [];
    for (const t of list) {
        if (!t || !t.src || !t.lang) continue;
        real.push({ language: t.lang, label: t.label || t.lang, src: t.src });
    }
    if (!real.length) return [];
    return real.concat([{ language: "", label: SUBTITLE_OFF_LABEL }]);
}

// ---- Quality (hls.js levels) ------------------------------------------------
//
// HLS is adaptive; the menu offers «Авто» (hls.currentLevel = -1, default) plus
// one entry per distinct rendition height, highest first. Labels match
// profile-new («Авто», `${height}p`).
export const QUALITY_AUTO_LABEL = "Авто";

// Map hls.levels into an ordered [{ label, level }] list: «Авто» first (level
// -1), then each distinct height descending (level = the hls level index).
// Height-less levels (audio-only) and duplicate heights are dropped. Returns
// just [«Авто»] when no usable heights exist — the caller hides the menu when
// the list has no real renditions.
export function mapQualityLevels(levels) {
    const list = Array.isArray(levels) ? levels : [];
    const seen = {};
    const items = [];
    for (let i = 0; i < list.length; i++) {
        const level = list[i];
        const height = level && level.height;
        if (!height || height <= 0) continue;
        const label = `${height}p`;
        if (seen[label]) continue; // dedup by height, keep the first index seen
        seen[label] = true;
        items.push({ label, level: i, height });
    }
    items.sort((a, b) => b.height - a.height);
    const result = [{ label: QUALITY_AUTO_LABEL, level: -1 }];
    for (const it of items) result.push({ label: it.label, level: it.level });
    return result;
}
